import React, { useState, useEffect } from 'react';
import './App.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Login from './Login';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function App() {
  // Function to shorten company names
  const shortenCompanyName = (name) => {
    if (!name) return name;
    
    const suffixes = [
      'Ordinary', 'Corporation', 'Corp', 'Limited', 'Ltd', 'Incorporated', 'Inc',
      'NV', 'Group', 'Grp', 'SE', 'Registered', 'SA', 'Co', 'plc', 'Holdings',
      'S.M.E', 'N.V', 'S.E', 'S.A', 'S.p.A', 'SOCIMI', 'Nom', 'Fund', 'PLC',
      'AG', 'AB'
    ];
    
    let result = name;
    let earliestIndex = result.length;
    
    for (let suffix of suffixes) {
      const escapedSuffix = suffix.replace(/\./g, '\\.?');
      const regex = new RegExp(`\\b${escapedSuffix}\\.?\\b`, 'i');
      const match = result.match(regex);
      
      if (match && match.index < earliestIndex) {
        earliestIndex = match.index;
      }
    }
    
    if (earliestIndex < result.length) {
      result = result.substring(0, earliestIndex);
    }
    
    result = result.replace(/[\s\-.,;:&]+$/g, '').trim();
    result = result.replace(/\s+/g, ' ');
    
    return result;
  };

  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [startDate, setStartDate] = useState('2024-07-30');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [showDollarGain, setShowDollarGain] = useState(true);

  const [dailyValues, setDailyValues] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [individualDailyValues, setIndividualDailyValues] = useState(null);
  const [loadingIndividualCharts, setLoadingIndividualCharts] = useState(false);

  const [token, setToken] = useState(localStorage.getItem('token'));

  const [showBenchmark, setShowBenchmark] = useState(false);
const [benchmarkMode, setBenchmarkMode] = useState('inception'); // 'inception' or 'startDate'
const [benchmarkValues, setBenchmarkValues] = useState(null);
const [loadingBenchmark, setLoadingBenchmark] = useState(false);
  
// Categorize cash account transactions
const categorizeCashTransaction = (description) => {
  const desc = description.toLowerCase();
  
  if (desc.includes('dividend')) return 'dividend';
  if (desc.includes('trade')) return 'trade';
  if (desc.includes('transfer')) return 'transfer';
  
  return 'other';
};

// Helper: Get date from transaction (handles both date and date_time formats)
const getTransactionDate = (transaction) => {
  if (transaction.date) return transaction.date;
  if (transaction.date_time) return transaction.date_time.split('T')[0];
  return null;
};

// Helper: Find matching transfer transaction in another account (±1 day)
const findMatchingTransfer = (transaction, accountName, allCashTransactions) => {
  const txnDate = getTransactionDate(transaction);
  if (!txnDate) return null;
  
  const description = transaction.description.toLowerCase();
  
  // Check if description mentions another account
  const accountNames = ['IBKR (AUD)', 'IBKR (USD)', 'IBKR (EUR)', 'IBKR (GBP)', 'HSBC (AUD)', 'HSBC (USD)', 'Morgans'];
  const mentionedAccount = accountNames.find(name => 
    description.includes(name.toLowerCase())
  );
  
  if (!mentionedAccount || mentionedAccount === accountName) return null;
  
  // Search for matching transaction in the mentioned account (±1 day)
  const matchingAccounts = allCashTransactions[mentionedAccount] || [];
  
  for (const match of matchingAccounts) {
    const matchDate = getTransactionDate(match);
    if (!matchDate) continue;
    
    const daysDiff = Math.abs((new Date(txnDate) - new Date(matchDate)) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 1 && categorizeCashTransaction(match.description) === 'transfer') {
      // Check if amounts are opposite signs (one positive, one negative)
      if ((transaction.amount > 0 && match.amount < 0) || (transaction.amount < 0 && match.amount > 0)) {
        return match;
      }
    }
  }
  
  return null;
};

// Helper: Get exchange rate from closest trade
const getExchangeRateFromTrades = (currency, targetDate, tradesData) => {
  if (currency === 'AUD') return 1;
  
  // Collect all trades in this currency with exchange rates
  const currencyTrades = [];
  
  Object.values(tradesData || {}).forEach(trades => {
    trades.forEach(trade => {
      const tradeCurrency = trade.brokerage_currency_code || 'AUD';
      const tradeCurr = ['AUD', 'EUR', 'GBP', 'USD'].includes(tradeCurrency) ? tradeCurrency : 'AUD';
      
      if (tradeCurr === currency && trade.exchange_rate && trade.transaction_date) {
        currencyTrades.push({
          date: trade.transaction_date,
          rate: trade.exchange_rate
        });
      }
    });
  });
  
  if (currencyTrades.length === 0) {
    console.warn(`No trades found for ${currency}, using 1.0 as fallback`);
    return 1;
  }
  
  // Find closest trade by date
  let closestTrade = currencyTrades[0];
  let minDiff = Math.abs(new Date(targetDate) - new Date(closestTrade.date));
  
  for (const trade of currencyTrades) {
    const diff = Math.abs(new Date(targetDate) - new Date(trade.date));
    if (diff < minDiff) {
      minDiff = diff;
      closestTrade = trade;
    }
  }
  
  return closestTrade.rate;
};

// Helper: Calculate transfer amount in AUD
const calculateTransferInAUD = (transaction, accountName, currency, allCashTransactions, tradesData) => {
  // If account is already AUD, return the amount as-is
  if (currency === 'AUD') return transaction.amount;
  
  // Try to find matching transfer in another account
  const matchingTransfer = findMatchingTransfer(transaction, accountName, allCashTransactions);
  
  // If matching transfer found and it's in AUD, use that amount
  if (matchingTransfer) {
    const matchAccounts = Object.entries(allCashTransactions);
    for (const [acctName, transactions] of matchAccounts) {
      if (acctName.includes('(AUD)') && transactions.includes(matchingTransfer)) {
        return matchingTransfer.amount;
      }
    }
  }
  
  // Otherwise, use exchange rate from closest trade
  const txnDate = getTransactionDate(transaction);
  const exchangeRate = getExchangeRateFromTrades(currency, txnDate, tradesData);
  
  return transaction.amount / exchangeRate;
};

  // Sorting state - default to descending closing balance
  const [sortState, setSortState] = useState({
    'Equities': { column: 'closing', ascending: false },
    'Infrastructure': { column: 'closing', ascending: false },
    'Property': { column: 'closing', ascending: false },
    'Unlisted Alternatives': { column: 'closing', ascending: false }
  });

  const fetchPortfolioData = (start = startDate, end = endDate) => {
  setLoading(true);
  setError(null);
  
  fetch(`${API_URL}/api/portfolio?start_date=${start}&end_date=${end}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.json())
    .then(data => {
      setPortfolioData(data);
      setLoading(false);
    })
    .catch(err => {
      setError(err.message);
      setLoading(false);
    });
};

const fetchDailyValues = (start = startDate, end = endDate) => {
  setLoadingChart(true);
  
  fetch(`${API_URL}/api/daily-values?start_date=${start}&end_date=${end}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.json())
    .then(data => {
      setDailyValues(data);
      setLoadingChart(false);
    })
    .catch(err => {
      console.error('Error fetching daily values:', err);
      setLoadingChart(false);
    });
};

const fetchIndividualDailyValues = (start = startDate, end = endDate) => {
  setLoadingIndividualCharts(true);
  
  fetch(`${API_URL}/api/daily-individual-values?start_date=${start}&end_date=${end}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.json())
    .then(data => {
      setIndividualDailyValues(data);
      setLoadingIndividualCharts(false);
    })
    .catch(err => {
      console.error('Error fetching individual daily values:', err);
      setLoadingIndividualCharts(false);
    });
};

const fetchBenchmarkValues = async (start = startDate, end = endDate) => {
  if (!showBenchmark) {
    console.log('Benchmark disabled, skipping fetch');
    return;
  }
  
  setLoadingBenchmark(true);
  
  // Determine opening balance and date based on mode
  let openingBalance, openingDate;
  
  if (benchmarkMode === 'inception') {
    openingDate = '2024-07-30';
    
    // Fetch the portfolio value on the inception date
    try {
      const inceptionResponse = await fetch(`${API_URL}/api/portfolio?start_date=${openingDate}&end_date=${openingDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const inceptionData = await inceptionResponse.json();
      openingBalance = inceptionData.openingData?.value || 972517.47;
      console.log('Fetched inception balance:', openingBalance);
    } catch (err) {
      console.error('Error fetching inception balance:', err);
      openingBalance = 972517.47;
    }
  } else {
    // FROM START DATE MODE
    openingDate = start;
    
    // FETCH the opening balance for the start date directly
    try {
      const startDateResponse = await fetch(`${API_URL}/api/portfolio?start_date=${openingDate}&end_date=${openingDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const startDateData = await startDateResponse.json();
      openingBalance = startDateData.openingData?.value;
      console.log('Fetched start date balance:', openingBalance, 'on', openingDate);
    } catch (err) {
      console.error('Error fetching start date balance:', err);
      openingBalance = portfolioData?.openingData?.value || 100000; // Fallback
    }
  }
  
  console.log('Fetching benchmark with:', { start, end, openingBalance, openingDate });
  
  fetch(`${API_URL}/api/benchmark-values?start_date=${start}&end_date=${end}&opening_balance=${openingBalance}&opening_date=${openingDate}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })
    .then(response => response.json())
    .then(data => {
      console.log('Benchmark data received:', data);
      setBenchmarkValues(data);
      setLoadingBenchmark(false);
    })
    .catch(err => {
      console.error('Error fetching benchmark values:', err);
      setLoadingBenchmark(false);
    });
};
  
  useEffect(() => {
  if (startDate && endDate && token) {
    fetchPortfolioData(startDate, endDate);
    fetchDailyValues(startDate, endDate);
    fetchIndividualDailyValues(startDate, endDate);
    if (showBenchmark) {
      fetchBenchmarkValues(startDate, endDate);  // ← Add parameters here!
    }
  }
}, [token, showBenchmark, benchmarkMode]);
  
  const handleUpdateClick = () => {
  fetchPortfolioData();
  fetchDailyValues();
  fetchIndividualDailyValues();
  if (showBenchmark) {
    fetchBenchmarkValues();
  }
};

  const handleLogout = () => {
  localStorage.removeItem('token');
  setToken(null);
};

  const setDateRange = (range) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    let fromDate = '';
    
    switch(range) {
      case '1d':
        const oneDayAgo = new Date(today);
        oneDayAgo.setDate(today.getDate() - 1);
        fromDate = oneDayAgo.toISOString().split('T')[0];
        break;
      case '1w':
        const oneWeekAgo = new Date(today);
        oneWeekAgo.setDate(today.getDate() - 7);
        fromDate = oneWeekAgo.toISOString().split('T')[0];
        break;
      case '1m':
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        fromDate = oneMonthAgo.toISOString().split('T')[0];
        break;
      case '3m':
        const threeMonthsAgo = new Date(today);
        threeMonthsAgo.setMonth(today.getMonth() - 3);
        fromDate = threeMonthsAgo.toISOString().split('T')[0];
        break;
      case '6m':
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        fromDate = sixMonthsAgo.toISOString().split('T')[0];
        break;
      case '12m':
        const twelveMonthsAgo = new Date(today);
        twelveMonthsAgo.setFullYear(today.getFullYear() - 1);
        fromDate = twelveMonthsAgo.toISOString().split('T')[0];
        break;
      case 'all':
        fromDate = '2024-07-30';
        break;
      default:
        return;
    }
    
    setStartDate(fromDate);
    setEndDate(todayStr);

    // Auto-fetch data immediately when using shortcut buttons
    setTimeout(() => {
  fetchPortfolioData(fromDate, todayStr);
  fetchDailyValues(fromDate, todayStr);
  fetchIndividualDailyValues(fromDate, todayStr);
  if (showBenchmark) {
    fetchBenchmarkValues(fromDate, todayStr);
  }
}, 0);
  };

  const getActiveRange = () => {
    const today = new Date().toISOString().split('T')[0];
    
    if (endDate !== today) return null;
    
    const daysDiff = Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
    
    if (daysDiff === 1) return '1d';
    if (daysDiff >= 6 && daysDiff <= 8) return '1w';
    if (daysDiff >= 28 && daysDiff <= 32) return '1m';
    if (daysDiff >= 89 && daysDiff <= 93) return '3m';
    if (daysDiff >= 180 && daysDiff <= 186) return '6m';
    if (daysDiff >= 360 && daysDiff <= 370) return '12m';
    if (startDate === '2024-07-30') return 'all';
    
    return null;
  };

  const handleSort = (portfolioName, column) => {
    setSortState(prev => {
      const current = prev[portfolioName];
      const isCurrentColumn = current?.column === column;
      
      return {
        ...prev,
        [portfolioName]: {
          column: column,
          ascending: isCurrentColumn ? !current.ascending : false
        }
      };
    });
  };

  if (!token) {
  return <Login onLogin={setToken} />;
}

  if (loading) return (
  <div className="loading-screen">
    <img src="/logo.png" alt="Logo" className="loading-logo" />
    <p className="loading-text">Loading portfolio data...</p>
  </div>
);
  if (error) return <div className="container">Error: {error}</div>;
  if (!portfolioData) return <div className="container">No data available</div>;

  const { openingData, closingData, tradesData, payoutsData, cashTransactions } = portfolioData;

  console.log('=== PORTFOLIO DATA CHECK ===');
console.log('cashTransactions exists?', !!cashTransactions);
console.log('cashTransactions:', cashTransactions);
console.log('===========================');

  // Create a map of instrument_id to holdings for quick lookup
  const openingHoldings = {};
  const closingHoldings = {};
  
  openingData?.holdings?.forEach(h => {
    openingHoldings[h.instrument_id] = h;
  });
  
  closingData?.holdings?.forEach(h => {
    closingHoldings[h.instrument_id] = h;
  });

  // Add cash accounts - ensure they're mapped to Unlisted Alternatives
  openingData?.cash_accounts?.forEach(c => {
    const cashKey = `cash_${c.id}`;
    openingHoldings[cashKey] = { 
      ...c, 
      isCash: true, 
      name: c.name || 'Cash Account',
      portfolio: 'Unlisted Alternatives',
      instrument_id: cashKey
    };
  });
  
  closingData?.cash_accounts?.forEach(c => {
    const cashKey = `cash_${c.id}`;
    closingHoldings[cashKey] = { 
      ...c, 
      isCash: true, 
      name: c.name || 'Cash Account',
      portfolio: 'Unlisted Alternatives',
      instrument_id: cashKey
    };
  });

  // Process payouts data
  let dividendOpening = 0;
  let dividendClosing = 0;
  let taxWithheld = 0;

  const dividendOpeningByCurrency = { 'AUD': 0, 'EUR': 0, 'GBP': 0, 'USD': 0 };
  const dividendsPaidByCurrency = { 'AUD': 0, 'EUR': 0, 'GBP': 0, 'USD': 0 };

  payoutsData?.forEach(payout => {
    const exDate = payout.goes_ex_on;
    const paidOn = payout.paid_on;
    const exchangeRate = payout.exchange_rate || 1;
    const currency = payout.currency || 'AUD';
    
    const amount = (payout.amount || 0) / exchangeRate;
    const grossAmount = (payout.gross_amount || 0) / exchangeRate;
    
    const taxAmount = grossAmount - amount;
    
    // Dividend Accruals Opening: ex_date <= startDate AND paid_on > startDate
    if (exDate <= startDate && paidOn > startDate) {
      dividendOpening += amount;
      const curr = ['AUD', 'EUR', 'GBP', 'USD'].includes(currency) ? currency : 'AUD';
      dividendOpeningByCurrency[curr] += amount;
    }
    
    // Dividend Accruals Closing: ex_date <= endDate AND paid_on > endDate
    if (exDate <= endDate && paidOn > endDate) {
      dividendClosing += amount;
    }
    
    // Tax Withheld: ex_date > startDate AND ex_date <= endDate (excluding start, including end)
    if (exDate > startDate && exDate <= endDate) {
      taxWithheld += taxAmount;
    }
    
    // Dividends paid: paid_on > startDate AND paid_on <= endDate (excluding start, including end)
if (paidOn > startDate && paidOn <= endDate) {
  const portfolioId = payout.portfolio_id?.toString();
  const curr = ['AUD', 'EUR', 'GBP', 'USD'].includes(currency) ? currency : 'AUD';
  
  // Only add to dividendsPaidByCurrency if NOT from Unlisted Alternatives (1092145)
  if (portfolioId !== '1092145') {
    dividendsPaidByCurrency[curr] += amount;
  }
}
  });

  // Separate tracking for HSBC accounts (Unlisted Alternatives only)
const tradesByCurrencyHSBC = { 'AUD': 0, 'EUR': 0, 'GBP': 0, 'USD': 0 };
const dividendsPaidByCurrencyHSBC = { 'AUD': 0, 'EUR': 0, 'GBP': 0, 'USD': 0 };

// Add trades from Unlisted Alternatives to HSBC totals
Object.entries(tradesData || {}).forEach(([portfolioName, trades]) => {
  if (portfolioName === 'Unlisted Alternatives') {
    trades.forEach(trade => {
      const currency = trade.brokerage_currency_code || 'AUD';
      const tradeValue = trade.value || 0;
      const curr = ['AUD', 'EUR', 'GBP', 'USD'].includes(currency) ? currency : 'AUD';
      tradesByCurrencyHSBC[curr] += tradeValue;
    });
  }
});

// Add dividends from Unlisted Alternatives to HSBC totals
payoutsData?.forEach(payout => {
  const paidOn = payout.paid_on;
  const portfolioId = payout.portfolio_id?.toString();
  const exchangeRate = payout.exchange_rate || 1;
  const currency = payout.currency || 'AUD';
  const amount = (payout.amount || 0) / exchangeRate;
  
  if (paidOn > startDate && paidOn <= endDate && portfolioId === '1092145') {
    const curr = ['AUD', 'EUR', 'GBP', 'USD'].includes(currency) ? currency : 'AUD';
    dividendsPaidByCurrencyHSBC[curr] += amount;
  }
});

  // Process trades by portfolio and instrument, and by currency
  const tradesByPortfolio = {};
  const tradesByCurrency = { 'AUD': 0, 'EUR': 0, 'GBP': 0, 'USD': 0 };
  
  Object.entries(tradesData || {}).forEach(([portfolioName, trades]) => {
    tradesByPortfolio[portfolioName] = {};
    trades.forEach(trade => {
      const instrumentId = trade.instrument_id;
      const currency = trade.brokerage_currency_code || 'AUD';
      const tradeValue = trade.value || 0;
      
      if (!tradesByPortfolio[portfolioName][instrumentId]) {
        tradesByPortfolio[portfolioName][instrumentId] = 0;
      }
      tradesByPortfolio[portfolioName][instrumentId] += tradeValue;
      
      // Only add to tradesByCurrency if NOT from Unlisted Alternatives
      if (portfolioName !== 'Unlisted Alternatives') {
        const curr = ['AUD', 'EUR', 'GBP', 'USD'].includes(currency) ? currency : 'AUD';
        tradesByCurrency[curr] += tradeValue;
      }
    });
  });

    // Build combined holdings by portfolio
  const portfolios = {
    'Equities': [],
    'Infrastructure': [],
    'Property': [],
    'Unlisted Alternatives': []
  };

  const allInstrumentIds = new Set([
    ...Object.keys(openingHoldings),
    ...Object.keys(closingHoldings)
  ]);

// Process cash account transactions to calculate transfers in AUD
const cashAccountTransfers = {
  'IBKR (AUD)': 0,
  'IBKR (USD)': 0,
  'IBKR (EUR)': 0,
  'IBKR (GBP)': 0,
  'HSBC (AUD)': 0,
  'HSBC (USD)': 0,
  'Morgans': 0
};

const currencyMap = {
  'IBKR (AUD)': 'AUD',
  'IBKR (USD)': 'USD',
  'IBKR (EUR)': 'EUR',
  'IBKR (GBP)': 'GBP',
  'HSBC (AUD)': 'AUD',
  'HSBC (USD)': 'USD',
  'Morgans': 'AUD'
};

// Process each cash account's transactions
Object.entries(cashTransactions || {}).forEach(([accountName, transactions]) => {
  const currency = currencyMap[accountName];
  
  transactions.forEach(transaction => {
    const txnDate = getTransactionDate(transaction);
    
    // Only include transactions within date range (exclude start, include end)
    if (txnDate > startDate && txnDate <= endDate) {
      const category = categorizeCashTransaction(transaction.description);
      
      if (category === 'transfer') {
        // Convert transfer to AUD
        const transferAUD = calculateTransferInAUD(transaction, accountName, currency, cashTransactions, tradesData);
        cashAccountTransfers[accountName] += transferAUD;
      }
    }
  });
});

console.log('=== CASH TRANSACTION DEBUG ===');
console.log('cashTransactions data:', cashTransactions);
console.log('Cash account transfers (in AUD):', cashAccountTransfers);
console.log('tradesByCurrency:', tradesByCurrency);
console.log('dividendsPaidByCurrency:', dividendsPaidByCurrency);
console.log('===========================');

  allInstrumentIds.forEach(instrumentId => {
    const opening = openingHoldings[instrumentId];
    const closing = closingHoldings[instrumentId];
    
    const portfolioName = closing?.portfolio || opening?.portfolio;
    
    if (!portfolioName || !portfolios[portfolioName]) return;

    const openingBalance = opening?.value || 0;
    const closingBalance = closing?.value || 0;
    const payoutGain = closing?.payout_gain || 0;
    
    let trades = tradesByPortfolio[portfolioName]?.[instrumentId] || 0;
    
    const isCash = opening?.isCash || closing?.isCash;
    const name = closing?.name || opening?.name || 'Unknown';
    
    let totalGain = 0;
    
    if (isCash && (name.startsWith('IBKR (') || name.startsWith('HSBC (') || name === 'Morgans')) {
  // Get account currency
  let currency = 'AUD';
  if (name.startsWith('IBKR (')) {
    const currencyMatch = name.match(/IBKR \(([A-Z]{3})\)/);
    currency = currencyMatch ? currencyMatch[1] : 'AUD';
  } else if (name.startsWith('HSBC (')) {
    const currencyMatch = name.match(/HSBC \(([A-Z]{3})\)/);
    currency = currencyMatch ? currencyMatch[1] : 'AUD';
  }
  
  // Calculate trades based on account type
  let securityTrades, dividendsPaid;
  
  if (name.startsWith('IBKR (')) {
    // IBKR uses trades/dividends from Equities, Infrastructure, Property
    securityTrades = -(tradesByCurrency[currency] || 0);
    dividendsPaid = (dividendsPaidByCurrency[currency] || 0);
  } else if (name.startsWith('HSBC (')) {
    // HSBC uses trades/dividends from Unlisted Alternatives only
    securityTrades = -(tradesByCurrencyHSBC[currency] || 0);
    dividendsPaid = (dividendsPaidByCurrencyHSBC[currency] || 0);
  } else {
    // Morgans has no trades or dividends
    securityTrades = 0;
    dividendsPaid = 0;
  }
  
  const transfers = cashAccountTransfers[name] || 0;
  
  trades = securityTrades + dividendsPaid + transfers;
  totalGain = closingBalance - openingBalance - trades;
  
  // DEBUG
  console.log(`Processing ${name}:`);
  console.log('  Security trades:', securityTrades);
  console.log('  Dividends paid:', dividendsPaid);
  console.log('  Transfers:', transfers);
  console.log('  TOTAL trades:', trades);
} else if (isCash) {
  // Other cash accounts (if any)
  trades = closingBalance - openingBalance - payoutGain;
  totalGain = closingBalance - openingBalance - trades;
} else {
  // Regular securities (non-cash)
  totalGain = closingBalance - openingBalance - trades;
}



    portfolios[portfolioName].push({
      name,
      instrumentId,
      openingBalance,
      trades,
      totalGain,
      payoutGain,
      closingBalance,
      isCash,
      isIBKR: isCash && name.startsWith('IBKR ('),
      isBalancingItem: false,
      isDividendAccrual: false,
      isTaxWithheld: false
    });
  });

  portfolios['Unlisted Alternatives'].push({
    name: 'Dividend Accruals',
    instrumentId: 'dividend_accruals',
    openingBalance: dividendOpening,
    trades: dividendClosing - dividendOpening,
    totalGain: 0,
    payoutGain: 0,
    closingBalance: dividendClosing,
    isCash: false,
    isIBKR: false,
    isBalancingItem: false,
    isDividendAccrual: true,
    isTaxWithheld: false
  });

  portfolios['Unlisted Alternatives'].push({
    name: 'Tax Withheld',
    instrumentId: 'tax_withheld',
    openingBalance: 0,
    trades: taxWithheld,
    totalGain: 0,
    payoutGain: 0,
    closingBalance: 0,
    isCash: false,
    isIBKR: false,
    isBalancingItem: false,
    isDividendAccrual: false,
    isTaxWithheld: true
  });

  const portfolioTotalsWithoutBalancing = {};
  Object.entries(portfolios).forEach(([name, holdings]) => {
    portfolioTotalsWithoutBalancing[name] = {
      openingBalance: holdings.reduce((sum, h) => sum + h.openingBalance, 0),
      trades: holdings.reduce((sum, h) => sum + h.trades, 0),
      totalGain: holdings.reduce((sum, h) => sum + h.totalGain, 0),
      payoutGain: holdings.reduce((sum, h) => sum + h.payoutGain, 0),
      closingBalance: holdings.reduce((sum, h) => sum + h.closingBalance, 0)
    };
  });

  const totalTradesExcludingBalancing = Object.values(portfolioTotalsWithoutBalancing).reduce((sum, p) => sum + p.trades, 0);
  const balancingItemTrades = 0;

  portfolios['Unlisted Alternatives'].push({
    name: 'Balancing item',
    instrumentId: 'balancing_item',
    openingBalance: 0,
    trades: balancingItemTrades,
    totalGain: 0,
    payoutGain: 0,
    closingBalance: 0,
    isCash: false,
    isIBKR: false,
    isBalancingItem: true,
    isDividendAccrual: false,
    isTaxWithheld: false
  });

  const portfolioTotals = {};
  Object.entries(portfolios).forEach(([name, holdings]) => {
    portfolioTotals[name] = {
      openingBalance: holdings.reduce((sum, h) => sum + h.openingBalance, 0),
      trades: holdings.reduce((sum, h) => sum + h.trades, 0),
      totalGain: holdings.reduce((sum, h) => sum + h.totalGain, 0),
      payoutGain: holdings.reduce((sum, h) => sum + h.payoutGain, 0),
      closingBalance: holdings.reduce((sum, h) => sum + h.closingBalance, 0)
    };
  });

  const grandTotals = {
    openingBalance: Object.values(portfolioTotals).reduce((sum, p) => sum + p.openingBalance, 0),
    trades: -taxWithheld,
    totalGain: Object.values(portfolioTotals).reduce((sum, p) => sum + p.totalGain, 0),
    payoutGain: Object.values(portfolioTotals).reduce((sum, p) => sum + p.payoutGain, 0),
    closingBalance: Object.values(portfolioTotals).reduce((sum, p) => sum + p.closingBalance, 0)
  };

  const ibkrGainLossTotal = portfolios['Unlisted Alternatives']
    .filter(h => h.isIBKR)
    .reduce((sum, h) => sum + h.totalGain, 0);

  const portfolioWeights = {};
  const portfolioTargets = {
    'Equities': 52.94,
    'Infrastructure': 23.53,
    'Property': 23.53
  };
  
  Object.entries(portfolioTotals).forEach(([name, totals]) => {
    portfolioWeights[name] = grandTotals.closingBalance !== 0 
      ? (totals.closingBalance / grandTotals.closingBalance * 100).toFixed(1)
      : '0.0';
  });

  // Calculate overweight/underweight
  const unlistedWeight = parseFloat(portfolioWeights['Unlisted Alternatives']);
  const adjustmentFactor = (100 - unlistedWeight) / 100;
  
  const portfolioOverUnder = {};
  Object.entries(portfolioTargets).forEach(([name, target]) => {
    const actualWeight = parseFloat(portfolioWeights[name]);
    const adjustedTarget = target * adjustmentFactor;
    const difference = actualWeight - adjustedTarget;
    portfolioOverUnder[name] = {
      difference: difference.toFixed(1),
      label: difference >= 0 ? 'o/w' : 'u/w'
    };
  });

  const checkBalance = (opening, trades, gain, closing) => {
    const calculated = opening + trades + gain;
    const variance = Math.abs(calculated - closing);
    const threshold = Math.abs(opening + trades) * 0.005;
    
    return {
      variance,
      threshold,
      failed: variance > threshold,
      largeVariance: variance >= 0.02
    };
  };

  const allHoldings = Object.values(portfolios).flat().filter(h => 
    !h.isBalancingItem && !h.isDividendAccrual && !h.isTaxWithheld
  );
  const balanceChecks = allHoldings.map(h => checkBalance(h.openingBalance, h.trades, h.totalGain, h.closingBalance));
  
  const holdingsWithLargeVariance = balanceChecks.filter(bc => bc.largeVariance).length;
  const percentWithLargeVariance = allHoldings.length > 0 ? (holdingsWithLargeVariance / allHoldings.length) : 0;
  const holdingsFailingThreshold = balanceChecks.filter(bc => bc.failed).length;
  
  const showGlobalWarning = percentWithLargeVariance > 0.5 || holdingsFailingThreshold > 0;

  const portfolioOrder = ['Equities', 'Infrastructure', 'Property', 'Unlisted Alternatives'];

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    const formatted = absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return value < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const formatGainOrDividend = (value, openingBalance, trades) => {
    if (showDollarGain) {
      return formatCurrency(value);
    } else {
      const base = openingBalance + trades;
      if (base === 0) {
        return formatCurrency(value);
      }
      const percent = (value / base) * 100;
      return formatPercent(percent);
    }
  };

  const getPercentValue = (value, openingBalance, trades) => {
    const base = openingBalance + trades;
    if (base === 0) return 0;
    return (value / base) * 100;
  };

  const getSortedHoldings = (holdings, portfolioName) => {
    const state = sortState[portfolioName];
    if (!state) return holdings;

    const sorted = [...holdings].sort((a, b) => {
      let aVal, bVal;
      
      switch(state.column) {
        case 'name':
          return state.ascending 
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        case 'opening':
          aVal = a.openingBalance;
          bVal = b.openingBalance;
          break;
        case 'trades':
          aVal = a.trades;
          bVal = b.trades;
          break;
        case 'gain':
          if (showDollarGain) {
            aVal = a.totalGain;
            bVal = b.totalGain;
          } else {
            aVal = getPercentValue(a.totalGain, a.openingBalance, a.trades);
            bVal = getPercentValue(b.totalGain, b.openingBalance, b.trades);
          }
          break;
        case 'dividend':
          if (showDollarGain) {
            aVal = a.payoutGain;
            bVal = b.payoutGain;
          } else {
            aVal = getPercentValue(a.payoutGain, a.openingBalance, a.trades);
            bVal = getPercentValue(b.payoutGain, b.openingBalance, b.trades);
          }
          break;
        case 'closing':
          aVal = a.closingBalance;
          bVal = b.closingBalance;
          break;
        default:
          return 0;
      }
      
      return state.ascending ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  };

  console.log('=== RENDER DEBUG ===');
console.log('showBenchmark:', showBenchmark);
console.log('benchmarkValues:', benchmarkValues);
console.log('benchmarkValues is array?', Array.isArray(benchmarkValues));

  return (
    <div className="container">
      <h1>Aerarivm Investments</h1>
      
<div className="controls">
  <button onClick={handleLogout} className="logout-button">
    Logout
  </button>
  
  <div className="date-controls">
    <div className="date-input">
      <label>From Date:</label>
      <input 
        type="date" 
        value={startDate} 
        onChange={(e) => setStartDate(e.target.value)}
      />
    </div>
    <div className="date-input">
      <label>To Date:</label>
      <input 
        type="date" 
        value={endDate} 
        onChange={(e) => setEndDate(e.target.value)}
      />
    </div>
    <div className="date-shortcuts">
      <button onClick={() => setDateRange('1d')} className={`shortcut-btn ${getActiveRange() === '1d' ? 'active' : ''}`}>1d</button>
      <button onClick={() => setDateRange('1w')} className={`shortcut-btn ${getActiveRange() === '1w' ? 'active' : ''}`}>1w</button>
      <button onClick={() => setDateRange('1m')} className={`shortcut-btn ${getActiveRange() === '1m' ? 'active' : ''}`}>1m</button>
      <button onClick={() => setDateRange('3m')} className={`shortcut-btn ${getActiveRange() === '3m' ? 'active' : ''}`}>3m</button>
      <button onClick={() => setDateRange('6m')} className={`shortcut-btn ${getActiveRange() === '6m' ? 'active' : ''}`}>6m</button>
      <button onClick={() => setDateRange('12m')} className={`shortcut-btn ${getActiveRange() === '12m' ? 'active' : ''}`}>12m</button>
      <button onClick={() => setDateRange('all')} className={`shortcut-btn ${getActiveRange() === 'all' ? 'active' : ''}`}>All</button>
    </div>
    <button onClick={handleUpdateClick} className="update-button">Update</button>
    <button 
  onClick={() => {
    setLoadingChart(true);
    setLoadingIndividualCharts(true);
    
    // Refresh main chart
    fetch(`${API_URL}/api/daily-values?start_date=${startDate}&end_date=${endDate}&force_refresh=true`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.json())
      .then(data => {
        setDailyValues(data);
        setLoadingChart(false);
      })
      .catch(err => {
        console.error('Error refreshing daily values cache:', err);
        setLoadingChart(false);
      });
    
    // Refresh individual charts
    fetch(`${API_URL}/api/daily-individual-values?start_date=${startDate}&end_date=${endDate}&force_refresh=true`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.json())
      .then(data => {
        setIndividualDailyValues(data);
        setLoadingIndividualCharts(false);
      })
      .catch(err => {
        console.error('Error refreshing individual cache:', err);
        setLoadingIndividualCharts(false);
      });
    
    alert('Cache refresh started for selected date range!');
  }} 
  className="refresh-cache-button"
  title="Refresh cached data for the selected date range"
>
  Refresh Cache
</button>
  </div>
  
  <div className="gain-toggle">
    <label>
      <input 
        type="radio" 
        checked={showDollarGain} 
        onChange={() => setShowDollarGain(true)}
      />
      $ Values
    </label>
    <label>
      <input 
        type="radio" 
        checked={!showDollarGain} 
        onChange={() => setShowDollarGain(false)}
      />
      % Values
    </label>
  </div>
</div>

<div className="benchmark-controls">
  <label>
    <input 
      type="checkbox" 
      checked={showBenchmark} 
      onChange={(e) => setShowBenchmark(e.target.checked)}
    />
    Show CPI+4.5% Benchmark
  </label>
  
  {showBenchmark && (
    <div className="benchmark-mode">
      <label>
        <input 
          type="radio" 
          checked={benchmarkMode === 'inception'} 
          onChange={() => setBenchmarkMode('inception')}
        />
        Since Inception (30/07/24)
      </label>
      <label>
        <input 
          type="radio" 
          checked={benchmarkMode === 'startDate'} 
          onChange={() => setBenchmarkMode('startDate')}
        />
        From Start Date
      </label>
    </div>
  )}
</div>

      <div className="total-value">
        <div className="total-header-row">
          <div className="total-header-item"></div>
          <div className="total-header-item">Opening</div>
          <div className="total-header-item">Contributions/Withdrawals</div>
          <div className="total-header-item">Gain/Loss</div>
          <div className="total-header-item">Dividends</div>
          <div className="total-header-item">Closing</div>
        </div>
        <div className="total-values-row">
          <div className="total-value-item total-label-cell">Total Portfolio</div>
          <div className="total-value-item">{formatCurrency(grandTotals.openingBalance)}</div>
          <div className="total-value-item">{formatCurrency(grandTotals.trades)}</div>
          <div className="total-value-item" style={{color: grandTotals.totalGain >= 0 ? '#28a745' : '#dc3545'}}>
            {showDollarGain 
              ? formatCurrency(grandTotals.totalGain)
              : formatPercent((grandTotals.totalGain / (grandTotals.openingBalance + grandTotals.trades)) * 100)
            }
          </div>
          <div className="total-value-item" style={{color: '#28a745'}}>
            {showDollarGain 
              ? formatCurrency(grandTotals.payoutGain)
              : formatPercent((grandTotals.payoutGain / (grandTotals.openingBalance + grandTotals.trades)) * 100)
            }
          </div>
          <div className="total-value-item">{formatCurrency(grandTotals.closingBalance)}</div>
        </div>
        {showGlobalWarning && (
          <div className="balance-warning">
            ⚠️ Warning: {holdingsFailingThreshold} holding(s) exceeded 0.50% variance threshold
            {percentWithLargeVariance > 0.5 && ` | ${Math.round(percentWithLargeVariance * 100)}% of holdings have variance ≥ $0.02`}
          </div>
        )}
      </div>

      {dailyValues && dailyValues.length > 0 && (
        <div className="chart-container">
          <h2>Portfolio Value Over Time</h2>
          <div className="chart-wrapper">
            {loadingChart ? (
              <div className="chart-loading">Loading chart data...</div>
            ) : (
              <Line
                data={{
                  labels: dailyValues.map(d => {
  const date = new Date(d.date);
  const daysDiff = Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
  
  if (daysDiff <= 90) {
    // 3 months or less: dd-mmm format
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day}-${month}`;
  } else {
    // More than 3 months: mmm-yy format
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  }
}),
datasets: [
  {
    label: 'Portfolio Value',
    data: dailyValues.map(d => d.value),
    borderColor: '#1B2A41',
    backgroundColor: 'rgba(27, 42, 65, 0.1)',
    fill: true,
    tension: 0.4,
    pointRadius: 0,
    pointHoverRadius: 5,
    pointHitRadius: 10
  },
  ...(showBenchmark && benchmarkValues && Array.isArray(benchmarkValues) && benchmarkValues.length > 0 ? [{
    label: 'CPI+4.5% Benchmark',
    data: benchmarkValues.map(d => d.value),
    borderColor: '#B8860B',
    backgroundColor: 'transparent',
    fill: false,
    tension: 0.4,
    pointRadius: 0,
    pointHoverRadius: 5,
    pointHitRadius: 10,
    borderWidth: 2
  }] : [])
]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return 'Value: $' + context.parsed.y.toLocaleString('en-US', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          });
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      grid: {
                        display: false
                      },
                      ticks: {
                        maxTicksLimit: 12
                      }
                    },
                    y: {
                      beginAtZero: false,
                      grid: {
                        color: '#e0e0e0'
                      },
                      ticks: {
                        callback: function(value) {
                          return '$' + (value / 1000).toFixed(0) + 'k';
                        }
                      }
                    }
                  },
                  elements: {
                    point: {
                      radius: 0,
                      hitRadius: 10,
                      hoverRadius: 5
                    }
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
      
{loadingChart && (!dailyValues || dailyValues.length === 0) && (
        <div className="chart-container">
          <h2>Portfolio Value Over Time</h2>
          <div className="chart-wrapper">
            <div className="chart-loading">
              Loading historical data... This may take a few minutes for long date ranges.
            </div>
          </div>
        </div>
      )}

      <div className="ibkr-check">
        IBKR Gain/Loss Total: {formatCurrency(ibkrGainLossTotal)}
      </div>
      
      <div className="portfolios-grid">
        {portfolioOrder.map(portfolioName => {
          const totals = portfolioTotals[portfolioName];
          const holdings = portfolios[portfolioName];
          const sortedHoldings = getSortedHoldings(holdings, portfolioName);
          const weight = portfolioWeights[portfolioName];
          
          const portfolioHoldingsChecks = holdings.filter(h => 
            !h.isBalancingItem && !h.isDividendAccrual && !h.isTaxWithheld
          ).map(h => 
            checkBalance(h.openingBalance, h.trades, h.totalGain, h.closingBalance)
          );
          const portfolioHoldingsFailingThreshold = portfolioHoldingsChecks.filter(bc => bc.failed).length;
          const portfolioHoldingsWithLargeVariance = portfolioHoldingsChecks.filter(bc => bc.largeVariance).length;
          const portfolioPercentWithLargeVariance = portfolioHoldingsChecks.length > 0 ? (portfolioHoldingsWithLargeVariance / portfolioHoldingsChecks.length) : 0;
          
          const showPortfolioWarning = portfolioPercentWithLargeVariance > 0.5 || portfolioHoldingsFailingThreshold > 0;
          
          const currentSort = sortState[portfolioName];
          
          return (
            <div key={portfolioName} className="portfolio-column">
              <div className="portfolio-header-new">
  <div className="portfolio-name-row">
    <strong>{portfolioName}</strong> ({weight}%{portfolioOverUnder[portfolioName] && `, ${portfolioOverUnder[portfolioName].difference}% ${portfolioOverUnder[portfolioName].label}`})
    {showPortfolioWarning && (
      <span className="balance-warning-inline"> ⚠️ {portfolioHoldingsFailingThreshold}</span>
    )}
  </div>
</div>
              
              {individualDailyValues && individualDailyValues[portfolioName] && individualDailyValues[portfolioName].length > 0 && (
                <div className="portfolio-chart-container">
                  <div className="portfolio-chart-wrapper">
                    {loadingIndividualCharts ? (
                      <div className="chart-loading-small">Loading...</div>
                    ) : (
                      <Line
                        data={{
                          labels: individualDailyValues[portfolioName].map(d => {
  const date = new Date(d.date);
  const daysDiff = Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
  
  if (daysDiff <= 90) {
    // 3 months or less: dd-mmm format
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day}-${month}`;
  } else {
    // More than 3 months: mmm-yy format
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  }
}),
                          datasets: [
                            {
                              label: portfolioName,
                              data: individualDailyValues[portfolioName].map(d => d.value),
                              borderColor: '#1B2A41',
                              backgroundColor: 'rgba(73, 78, 82, 0.1)',
                              fill: true,
                              tension: 0.4,
                              pointRadius: 0,
                              pointHoverRadius: 5,
                              pointHitRadius: 10
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: false
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return 'Value: $' + context.parsed.y.toLocaleString('en-US', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2 
                                  });
                                }
                              }
                            }
                          },
                          scales: {
                            x: {
                              grid: {
                                display: false
                              },
                              ticks: {
                                maxTicksLimit: 4,
                                font: {
                                  size: 10
                                }
                              }
                            },
                            y: {
                              beginAtZero: false,
                              grid: {
                                color: '#e0e0e0'
                              },
                              ticks: {
                                maxTicksLimit: 5,
                                font: {
                                  size: 10
                                },
                                callback: function(value) {
                                  return '$' + (value / 1000).toFixed(0) + 'k';
                                }
                              }
                            }
                          },
                          elements: {
                            point: {
                              radius: 0,
                              hitRadius: 10,
                              hoverRadius: 5
                            }
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              
              <table>
                <thead>
                  <tr className="portfolio-totals-row">
                    <td></td>
                    <td className="portfolio-total-cell">{formatCurrency(totals.openingBalance)}</td>
                    <td className="portfolio-total-cell">{formatCurrency(totals.trades)}</td>
                    <td className="portfolio-total-cell" style={{color: totals.totalGain >= 0 ? '#28a745' : '#dc3545'}}>
                      {showDollarGain 
                        ? formatCurrency(totals.totalGain)
                        : formatPercent((totals.totalGain / (totals.openingBalance + totals.trades)) * 100)
                      }
                    </td>
                    <td className="portfolio-total-cell" style={{color: '#28a745'}}>
                      {showDollarGain 
                        ? formatCurrency(totals.payoutGain)
                        : formatPercent((totals.payoutGain / (totals.openingBalance + totals.trades)) * 100)
                      }
                    </td>
                    <td className="portfolio-total-cell">{formatCurrency(totals.closingBalance)}</td>
                  </tr>
                  <tr>
                    <th onClick={() => handleSort(portfolioName, 'name')} className="sortable">
                      Name {currentSort?.column === 'name' && (currentSort.ascending ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort(portfolioName, 'opening')} className="sortable">
                      Opening {currentSort?.column === 'opening' && (currentSort.ascending ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort(portfolioName, 'trades')} className="sortable">
                      Trades {currentSort?.column === 'trades' && (currentSort.ascending ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort(portfolioName, 'gain')} className="sortable">
                      Gain/Loss {currentSort?.column === 'gain' && (currentSort.ascending ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort(portfolioName, 'dividend')} className="sortable">
                      Dividends {currentSort?.column === 'dividend' && (currentSort.ascending ? '▲' : '▼')}
                    </th>
                    <th onClick={() => handleSort(portfolioName, 'closing')} className="sortable">
                      Closing {currentSort?.column === 'closing' && (currentSort.ascending ? '▲' : '▼')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHoldings.length > 0 ? (
                    sortedHoldings.map((holding, index) => {
                      const gainColor = holding.totalGain >= 0 ? '#28a745' : '#dc3545';
                      const isSpecialItem = holding.isBalancingItem || holding.isDividendAccrual || holding.isTaxWithheld;
                      const balanceCheck = isSpecialItem ? { failed: false } : checkBalance(holding.openingBalance, holding.trades, holding.totalGain, holding.closingBalance);
                      
                      return (
                        <tr key={index} className={balanceCheck.failed ? 'balance-error' : ''}>
                          <td>{isSpecialItem ? <em>{holding.name}</em> : shortenCompanyName(holding.name)}</td>
                          <td>{formatCurrency(holding.openingBalance)}</td>
                          <td>{formatCurrency(holding.trades)}</td>
                          <td style={{color: gainColor}}>
                            {formatGainOrDividend(holding.totalGain, holding.openingBalance, holding.trades)}
                          </td>
                          <td style={{color: '#28a745'}}>
                            {formatGainOrDividend(holding.payoutGain, holding.openingBalance, holding.trades)}
                          </td>
                          <td>{formatCurrency(holding.closingBalance)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" style={{textAlign: 'center', color: '#999'}}>No holdings</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default App;